/* eslint-disable @typescript-eslint/no-explicit-any */
import { JWT_KEY } from 'shared/constants';

/**
 * @param {string} email 이메일 정규식 유효성 검사
 * @returns {boolean}
 */
export const isValidEmail = (email: string) => {
  return email.match(/^[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*@[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*.[a-zA-Z]{2,4}$/i) !== null;
};

export const isValidPassword = (password: string) => {
  return password.match(/^(?=.*[a-zA-Z])(?=.*[!@#$%^*+=-])(?=.*[0-9]).{8,25}$/);
};

/**
 * @param {string} phoneNumber 전화번호 정규식 유효성 검사
 * @returns {boolean}
 */
export const isValidPhoneNumber = (phoneNumber: string) => {
  return phoneNumber.match(/^01(?:0|1|[6-9])-(?:\d{3}|\d{4})-\d{4}$/);
};

/**
 * @param {string} input 전화번호 형식으로 정규식 변환
 * @returns {string|undefined}
 */
export const formatPhoneNumber = (input?: string) => {
  return input
    ?.replace(/[^0-9]/g, '')
    .replace(/^(\d{0,3})(\d{0,4})(\d{0,4})$/g, '$1-$2-$3')
    .replace(/(-{1,2})$/g, '');
};

/**
 * 국가번호를 표기 형태로 감싸 전화번호와 합친다.
 * 국가번호가 없는(구버전 응답) 건은 전화번호만 그대로 노출한다.
 */
const joinPhoneNumberWithCountryCode = (
  countryCode: string | null | undefined,
  phoneNumber: string | null | undefined,
  wrapCode: (code: string) => string,
) => {
  const phone = phoneNumber?.trim() ?? '';
  const code = countryCode?.trim().replace(/^\+/, '') ?? '';

  if (!code) return phone;
  if (!phone) return wrapCode(code);

  return `${wrapCode(code)} ${phone}`;
};

/**
 * 화면 표기용. 국가번호와 전화번호를 '+82 01012345678' 형태로 합친다.
 * @param {string} countryCode 국가번호. '82' / '+82' 둘 다 허용
 * @param {string} phoneNumber 전화번호
 * @returns {string}
 */
export const formatPhoneNumberWithCountryCode = (countryCode?: string | null, phoneNumber?: string | null) =>
  joinPhoneNumberWithCountryCode(countryCode, phoneNumber, (code) => `+${code}`);

/**
 * CSV(엑셀 내보내기)용. '(+82) 01012345678' 형태로 합친다.
 * Excel은 '+'로 시작하는 셀을 수식으로 해석해 #NAME? 를 띄우므로(따옴표로도 못 막음)
 * 화면과 달리 국가번호를 괄호로 감싸 텍스트로 인식되게 한다.
 * @param {string} countryCode 국가번호. '82' / '+82' 둘 다 허용
 * @param {string} phoneNumber 전화번호
 * @returns {string}
 */
export const formatPhoneNumberWithCountryCodeForCsv = (countryCode?: string | null, phoneNumber?: string | null) =>
  joinPhoneNumberWithCountryCode(countryCode, phoneNumber, (code) => `(+${code})`);

export const getJwt = () => window.localStorage.getItem(JWT_KEY) ?? '';

export const saveJwt = (jwt: string) => {
  window.localStorage.setItem(JWT_KEY, jwt);
};

export const clearJwt = () => {
  window.localStorage.removeItem(JWT_KEY ?? '');
};

/*export const saveUser = (user: GetUsersUserIdResponse) => {
  window.localStorage.setItem(
    process.env.REACT_APP_LOGIN_INFO ?? "",
    JSON.stringify(user) ?? ""
  );
};*/

export const clearUser = () => {
  window.localStorage.removeItem(process.env.REACT_APP_LOGIN_INFO ?? '');
};

export const getUserEmail = () => {
  const loginInfoValue = window.localStorage.getItem(process.env.REACT_APP_LOGIN_INFO ?? '');
  if (loginInfoValue === null) {
    return '';
  } else {
    const loginInfo = JSON.parse(loginInfoValue);
    return loginInfo.email ?? '';
  }
};

export const getUserName = (): string => {
  const loginInfoValue = window.localStorage.getItem(process.env.REACT_APP_LOGIN_INFO ?? '');
  if (loginInfoValue === null) {
    return '';
  } else {
    const loginInfo = JSON.parse(loginInfoValue);
    return loginInfo.name ?? '';
  }
};

declare global {
  interface Window {
    daum: any;
  }
}

export function execDaumPostcode(callback: (address: string, addressEN: string, sigunguCode?: string) => void) {
  new window.daum.Postcode({
    oncomplete: function (data: any) {
      // 팝업에서 검색결과 항목을 클릭했을때 실행할 코드를 작성하는 부분.

      // 각 주소의 노출 규칙에 따라 주소를 조합한다.
      // 내려오는 변수가 값이 없는 경우엔 공백('')값을 가지므로, 이를 참고하여 분기 한다.
      let addr = ''; // 주소 변수
      let extraAddr = ''; // 참고항목 변수

      let addrEn = '';
      let sigunguCode = ''; // 시군구 코드

      //사용자가 선택한 주소 타입에 따라 해당 주소 값을 가져온다.
      if (data.userSelectedType === 'R') {
        // 사용자가 도로명 주소를 선택했을 경우
        addr = data.roadAddress;
        addrEn = data.roadAddressEnglish;
        sigunguCode = data.sigunguCode;
      } else {
        // 사용자가 지번 주소를 선택했을 경우(J)
        addr = data.jibunAddress;
        addrEn = data.jibunAddressEnglish;
        sigunguCode = data.sigunguCode;
      }

      // 사용자가 선택한 주소가 도로명 타입일때 참고항목을 조합한다.
      if (data.userSelectedType === 'R') {
        // 법정동명이 있을 경우 추가한다. (법정리는 제외)
        // 법정동의 경우 마지막 문자가 "동/로/가"로 끝난다.
        if (data.bname !== '' && /[동|로|가]$/g.test(data.bname)) {
          extraAddr += data.bname;
        }
        // 건물명이 있고, 공동주택일 경우 추가한다.
        if (data.buildingName !== '' && data.apartment === 'Y') {
          extraAddr += extraAddr !== '' ? ', ' + data.buildingName : data.buildingName;
        }
        // 표시할 참고항목이 있을 경우, 괄호까지 추가한 최종 문자열을 만든다.
        if (extraAddr !== '') {
          extraAddr = ' (' + extraAddr + ')';
        }
        // 조합된 참고항목을 해당 필드에 넣는다.
        // addr += extraAddr;
      }

      callback(addr, addrEn, sigunguCode);
    },
  }).open();
}
